/**
 * @module components/InvoiceView
 * @description Complete invoice view component for order history.
 * Displays invoice status, payment button, and polling for status updates.
 */

import React, { useCallback } from 'react';
import { InvoiceButton } from 'src/components/InvoiceButton';
import { InvoiceStatusBadge } from 'src/components/InvoiceStatusBadge';
import { SanityBadge } from 'src/components/SanityBadge';
import InvoiceStatusPoller from 'src/pages/Order/InvoiceStatusPoller';
import { Sanity } from 'src/types/sanity';
import { openLink } from 'src/utils';
import * as Styles from './styles';
import { InvoiceViewProps } from './types';

/**
 * @component InvoiceView
 * @summary Complete invoice display with status, payment link, and status polling.
 * Shows invoice provider, status badge, and payment button (if payment_url available).
 * Continuously polls invoice status to detect payment state changes.
 * @type {React.FC<InvoiceViewProps>}
 * @param {InvoiceViewProps} props - Component props from types.ts
 * @returns {React.ReactElement} Invoice view with status and payment action
 */
export const InvoiceView: React.FC<InvoiceViewProps> = ({
  invoice,
  orderId,
  index,
  totalCount = 1,
  onReplaceOrder,
  className,
  ...rest
}) => {
  // const providerName = useMemo(() => {
  //   if (invoice.invoiceProvider) {
  //     return Invoices.Types.ProviderNames[invoice.invoiceProvider as Invoices.Types.Providers];
  //   }
  //   return 'Unknown';
  // }, [invoice.invoiceProvider]);

  const handleOpenPaymentLink = useCallback(() => {
    const linkUrl = invoice.payment_url || invoice.url;
    if (!linkUrl) return;
    openLink(linkUrl)();
  }, [invoice.payment_url, invoice.url]);

  const parts: React.ReactNode[] = [];
  const actions: React.ReactNode[] = [];

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('InvoiceView invoice data:', {
      id: invoice.id,
      payment_url: invoice.payment_url,
      url: invoice.url,
      status: invoice.status,
      paid: invoice.paid,
    });
  }

  // Invoice status badges
  parts.push(
    <Styles.BadgeGroup key={`invoice-status-${invoice.id}`}>
      {invoice.number && (
        <Styles.BadgeItem>
          <SanityBadge
            tone={Sanity.Types.SanityTone.PRIMARY}
            outline
          >
            {invoice.number}
          </SanityBadge>
        </Styles.BadgeItem>
      )}
      <Styles.BadgeItem>
        <InvoiceStatusBadge invoice={invoice} />
      </Styles.BadgeItem>
    </Styles.BadgeGroup>,
  );

  // Payment button (if payment_url or url exists)
  const invoiceLink = invoice.payment_url || invoice.url;
  if (invoiceLink) {
    actions.push(
      <InvoiceButton
        key={`invoice-${invoice.id}-payment`}
        invoice={invoice}
        onOpenPaymentLink={handleOpenPaymentLink}
      />,
    );
  }

  if (actions.length > 0) {
    parts.push(
      <Styles.Actions key={`invoice-${invoice.id}-actions`}>
        {actions}
      </Styles.Actions>,
    );
  }

  // Headless poller for invoice status
  parts.push(
    <InvoiceStatusPoller
      key={`invoice-poller-${invoice.id}`}
      orderId={orderId}
      invoice={invoice}
      onReplaceOrder={onReplaceOrder}
    />,
  );

  return (
    <Styles.Container className={className} {...rest}>
      {parts}
    </Styles.Container>
  );
};

export default InvoiceView;
